import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Box, TextField, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  MDXEditor,
  MDXEditorMethods,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  tablePlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
  diffSourcePlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  CreateLink,
  InsertImage,
  InsertTable,
  InsertCodeBlock,
  InsertThematicBreak,
  ListsToggle,
  DiffSourceToggleWrapper,
  Separator,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { ProductExampleAttachmentDto } from 'apiClient/Api';
import { buildAttachmentRef, useAttachmentTokens } from 'Hooks/useAttachmentTokens';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Example id — only for callers that store images in the attachment pipeline. */
  exampleId?: string | null;
  attachments?: ProductExampleAttachmentDto[];
  /** Uploads a file and returns the stored attachment — used by drag/drop and the toolbar upload button. */
  onUpload?: (file: File) => Promise<ProductExampleAttachmentDto | null>;
  placeholder?: string;
  disabled?: boolean;
  /** Height of the writing area. Default fits a long offer text; notes want less. */
  minHeight?: number;
}

const CODE_BLOCK_LANGUAGES = {
  '': 'Text',
  xml: 'XML',
  json: 'JSON',
  yaml: 'YAML',
  ini: 'INI',
  js: 'JavaScript',
  ts: 'TypeScript',
  bash: 'Bash',
  csharp: 'C#',
};

/**
 * WYSIWYG Markdown editor backed by @mdxeditor/editor.
 *
 * The attachment pipeline is optional. Callers that pass `exampleId` + `onUpload`
 * get file uploads: images dragged/dropped or pasted are sent to
 * `POST /ProductExample/{id}/attachments`, and the stored markdown uses the
 * `attachment:{id}` scheme (which MarkdownRenderer resolves to a short-lived
 * token URL). Callers that pass neither — offer texts, call notes — get the same
 * editor with image *URLs* only, which is all those texts need.
 */
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  exampleId,
  attachments,
  onUpload,
  placeholder,
  disabled,
  minHeight = 420,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = useAttachmentTokens(exampleId ?? null);
  const editorRef = useRef<MDXEditorMethods>(null);

  /**
   * The source the MDX parser refused, if any.
   *
   * MDXEditor parses its markdown as MDX, so anything that *looks* like a JSX tag has to be one.
   * `<name@firma.de>` is a perfectly good CommonMark autolink and a syntax error in MDX — `@` is
   * not a name character — and the editor then renders its own error in place of the writing area.
   * That is the wrong trade for us: these are call notes and offer texts, and one stray `<` must
   * not lock the author out of the whole document.
   *
   * So on a parse error we fall back to editing the raw markdown. Deliberately no rewriting of the
   * stored text: it is rendered as HTML, Markdown *and* RTF downstream, and an escape that fixes
   * the editor would show up as literal noise in the generated offer.
   */
  const [rejected, setRejected] = useState<{ source: string; error: string } | null>(null);

  const handleError = useCallback((payload: { error: string; source: string }) => {
    // Deferred: the parser reports this while rendering, and setting state there warns.
    queueMicrotask(() => setRejected({ source: payload.source, error: payload.error }));
  }, []);

  // Once the text no longer matches what was refused, give the rich editor another try — that is
  // what happens when the author repairs the offending line in the fallback.
  const parseFailed = rejected !== null && rejected.source === value;

  // Keep the editor markdown in sync when the parent replaces the string
  // (e.g. after a save that re-hydrates form state from the server).
  React.useEffect(() => {
    if (parseFailed) return;
    const current = editorRef.current?.getMarkdown();
    if (current !== undefined && current !== value) {
      editorRef.current?.setMarkdown(value);
    }
  }, [value, parseFailed]);

  const attachmentIndex = useMemo(() => {
    const map = new Map<string, ProductExampleAttachmentDto>();
    (attachments ?? []).forEach((a) => { if (a.id) map.set(a.id, a); });
    return map;
  }, [attachments]);

  const imageUploadHandler = useCallback(async (file: File): Promise<string> => {
    if (!onUpload) throw new Error('upload handler missing');
    const att = await onUpload(file);
    if (!att?.id) throw new Error('upload failed');
    return buildAttachmentRef(att.id);
  }, [onUpload]);

  const imagePreviewHandler = useCallback(async (src: string): Promise<string> => {
    if (!src.startsWith('attachment:')) return src;
    const id = src.slice('attachment:'.length);
    if (!attachmentIndex.has(id)) return src;
    const resolved = await tokens.awaitToken(id);
    return resolved ?? src;
  }, [attachmentIndex, tokens]);

  const plugins = useMemo(() => [
    headingsPlugin(),
    listsPlugin(),
    quotePlugin(),
    thematicBreakPlugin(),
    linkPlugin(),
    linkDialogPlugin(),
    tablePlugin(),
    // The plugin stays loaded even without uploads, so an image already in the
    // markdown still renders instead of tripping the editor; only the upload
    // half of the insert dialog disappears.
    imagePlugin({
      imagePreviewHandler,
      ...(onUpload ? { imageUploadHandler } : {}),
    }),
    codeBlockPlugin({ defaultCodeBlockLanguage: '' }),
    codeMirrorPlugin({ codeBlockLanguages: CODE_BLOCK_LANGUAGES }),
    diffSourcePlugin({ viewMode: 'rich-text' }),
    markdownShortcutPlugin(),
    toolbarPlugin({
      toolbarContents: () => (
        <DiffSourceToggleWrapper>
          <UndoRedo/>
          <Separator/>
          <BoldItalicUnderlineToggles/>
          <Separator/>
          <BlockTypeSelect/>
          <Separator/>
          <ListsToggle/>
          <Separator/>
          <CreateLink/>
          <InsertImage/>
          <InsertTable/>
          <InsertThematicBreak/>
          <InsertCodeBlock/>
        </DiffSourceToggleWrapper>
      ),
    }),
  ], [imagePreviewHandler, imageUploadHandler, onUpload]);

  // Uploads were asked for, but the example does not exist yet — an editor whose
  // drag/drop silently fails would be worse than saying so.
  if (onUpload && !exampleId) {
    return (
      <Alert severity="info">{t('productExamples.attachments.needsDraft')}</Alert>
    );
  }

  // The rich editor could not read this text. Editing the source beats showing an error and
  // nothing else — the author can fix the offending line and gets the rich editor back.
  if (parseFailed) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('markdownEditor.parseFailed')}
        </Alert>
        <TextField
          fullWidth
          multiline
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          slotProps={{
            input: {
              sx: { fontFamily: 'monospace', fontSize: '0.85rem', alignItems: 'flex-start' },
            },
            htmlInput: { style: { minHeight } },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      className={theme.palette.mode === 'dark' ? 'dark-theme dark-editor' : undefined}
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        '& .mdxeditor': {
          '--accentBase': theme.palette.primary.main,
          '--accentBgSubtle': theme.palette.action.hover,
          '--accentBgHover': theme.palette.action.selected,
          '--accentText': theme.palette.primary.main,
          '--accentTextContrast': theme.palette.primary.contrastText,
          '--baseBg': theme.palette.background.paper,
          '--baseText': theme.palette.text.primary,
          '--baseTextContrast': theme.palette.text.primary,
          bgcolor: 'background.paper',
          color: 'text.primary',
          minHeight: minHeight + 60,
        },
        '& .mdxeditor-toolbar': {
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
          flexWrap: 'wrap',
        },
        '& .mdxeditor-root-contenteditable': {
          minHeight,
          padding: 2,
        },
        '& .mdxeditor-root-contenteditable > div': {
          outline: 'none',
        },
      }}
    >
      <MDXEditor
        ref={editorRef}
        markdown={value}
        onChange={onChange}
        onError={handleError}
        placeholder={placeholder ?? t('productExamples.edit.contentPlaceholder')}
        readOnly={disabled}
        plugins={plugins}
        contentEditableClassName="mdx-content"
      />
    </Box>
  );
};
